import * as XLSX from 'xlsx';
import { toDateInputValue, toLocalDateInputValue } from '@/lib/dates';
// The shared workbook toolkit: header-row autofilter, columns sized to their
// widest cell, and the exporter's local date for the file name.
import { sheet as buildSheet, stamp, type Cell } from '@/modules/manpower/lib/manpowerExport';
import type { Equipment } from './EquipmentPage';

/**
 * Excel export of the Equipment page (#571 review): exactly the rows on screen,
 * in the order shown — search, status filter and "Include completed" applied,
 * grouped by job as the table groups them. Clearing the filters and ticking
 * "Include completed" exports everything. Same contract as the Projects export.
 *
 * Fuller than the table: every field the request form carries, so the
 * spreadsheet can answer what the grid can't (PO numbers, cost codes, delivery
 * instructions, notes).
 *
 * ⚠️ The Rate column is the rental-terms rule again (rentalTermsAccess.ts):
 * present only for equipment editors and Admin. For everyone else the API
 * already sends the rate as null, but an all-blank Rate column would read as
 * "no rates set", so it is left out instead.
 *
 * Dates:
 *  - calendar dates (delivery, start, end, last inspection) are written as
 *    YYYY-MM-DD, so Excel sorts them chronologically;
 *  - real moments (called off, submitted) are reduced to the date they fell on
 *    in the exporter's zone. A UTC slice would date an 8pm-Eastern call-off to
 *    the next day.
 */

interface Column {
  header: string;
  value: (e: Equipment) => Cell | null | undefined;
  /** Equipment editors and Admin only (rentalTermsAccess.ts). */
  rentalTerms?: true;
}

/** Excel refuses a cell over 32,767 characters; notes accumulate call-off and extension entries. */
const MAX_CELL = 32_000;

const day = (iso: string | null | undefined) => toDateInputValue(iso) || '';
const momentDay = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : toLocalDateInputValue(d);
};
const text = (s: string | null | undefined) => (s ? s.slice(0, MAX_CELL) : '');

const COLUMNS: Column[] = [
  { header: 'Job #',                  value: (e) => e.project?.jobNumber ?? '' },
  { header: 'Job',                    value: (e) => e.project?.title ?? e.jobNameRaw ?? '' },
  { header: 'Equipment',              value: (e) => e.title },
  { header: 'Equipment Type',         value: (e) => e.equipmentType },
  { header: 'Status',                 value: (e) => e.status?.label },
  { header: 'Rental / Owned',         value: (e) => e.rentalType?.label },
  { header: 'Owned Eq #',             value: (e) => e.ownedEquipmentNumber },
  { header: 'Quantity',               value: (e) => e.quantity },
  { header: 'Vendor',                 value: (e) => e.vendor },
  { header: 'Vendor Contact',         value: (e) => e.vendorContactInfo },
  { header: 'Rate',                   value: (e) => e.rate, rentalTerms: true },
  { header: 'Billing Cycle',          value: (e) => e.billingCycle?.label },
  { header: 'Duration',               value: (e) => e.duration },
  { header: 'Delivery Date',          value: (e) => day(e.deliveryDate) },
  { header: 'Start Date',             value: (e) => day(e.startDate) },
  { header: 'End Date',               value: (e) => day(e.endDate) },
  { header: 'Called Off',             value: (e) => momentDay(e.calledOffAt) },
  { header: 'PO #',                   value: (e) => e.poNumber },
  { header: 'Return PO #',            value: (e) => e.returnPoNumber },
  { header: 'Cost Code',              value: (e) => e.costCode },
  { header: 'Requested By',           value: (e) => e.requestedBy },
  { header: 'Description',            value: (e) => text(e.description) },
  { header: 'Attachments / Accessories', value: (e) => text(e.attachmentDescription) },
  { header: 'Alt. Delivery Address',  value: (e) => e.altDeliveryAddress },
  { header: 'Delivery Specifics',     value: (e) => text(e.deliverySpecifics) },
  { header: 'Driver Instructions',    value: (e) => text(e.driverInstructions) },
  { header: 'Last Inspection',        value: (e) => day(e.lastInspectionDate) },
  { header: 'Notes',                  value: (e) => text(e.notes) },
  { header: 'Submitted',              value: (e) => momentDay(e.createdAt) },
];

/** The sheet as rows of cells, header first. Pure, so it can be tested without Excel. */
export function equipmentExportRows(rows: Equipment[], includeRentalTerms: boolean): Cell[][] {
  const cols = COLUMNS.filter((c) => includeRentalTerms || !c.rentalTerms);
  return [
    cols.map((c) => c.header),
    ...rows.map((e) => cols.map((c) => c.value(e) ?? '')),
  ];
}

/** Build the workbook and download it as equipment-export-YYYY-MM-DD.xlsx. */
export function exportEquipmentToXlsx(rows: Equipment[], includeRentalTerms: boolean): void {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildSheet(equipmentExportRows(rows, includeRentalTerms), { filter: true }), 'Equipment');
  XLSX.writeFile(wb, `equipment-export-${stamp()}.xlsx`);
}
